import { Fragment, useEffect } from 'react';
import { Link, useLocation } from 'react-router';
import { useBreadcrumbTrail } from '../lib/breadcrumb-context.js';
import { BrandSelector } from './BrandSelector.js';
import { navGroups } from './Sidebar.js';
import { APP_NAME } from './shell/AppMark.js';

/**
 * Spec 229 — "Gjensidige › Sources › Domains".
 *
 * The brand is the FIRST crumb because every page is scoped to one brand, and
 * that scope is exactly what a breadcrumb expresses. Before this it was a
 * separate control competing with the page title for the same corner.
 *
 * The fallback reads the sidebar's own route table rather than a second map, so
 * a destination has one name in the app, not two that can drift.
 */
function useFallbackTrail(): { label: string }[] {
  const { pathname } = useLocation();
  let best: { to: string; label: string } | null = null;
  for (const group of navGroups) {
    for (const item of group.items) {
      if (pathname === item.to || pathname.startsWith(item.to + '/')) {
        if (!best || item.to.length > best.to.length) best = item;
      }
    }
  }
  return best ? [{ label: best.label }] : [];
}

export function BarCrumb() {
  const published = useBreadcrumbTrail();
  const fallback = useFallbackTrail();
  const trail = published.length > 0 ? published : fallback;

  // Spec 247 — the tab title is "Page — App" (Kridt layout.md), page first
  // because tabs truncate from the end. Every page read "GEO Observatory", so
  // several open tabs could not be told apart. Derived from THIS trail rather
  // than a second map, for the reason this file gives: one name per page.
  const page = trail.length > 0 ? trail[trail.length - 1]!.label : '';
  useEffect(() => {
    document.title = page ? `${page} — ${APP_NAME}` : APP_NAME;
  }, [page]);

  return (
    <nav className="hub-crumb" aria-label="Breadcrumb" data-testid="bar-crumb">
      <BrandSelector />
      {trail.map((item, idx) => {
        const isLast = idx === trail.length - 1;
        const to = 'to' in item ? (item as { to?: string }).to : undefined;
        return (
          <Fragment key={`${idx}-${item.label}`}>
            <i aria-hidden="true" />
            {isLast || !to ? (
              <b data-testid={`bar-crumb-${idx}`} aria-current={isLast ? 'page' : undefined}>
                {item.label}
              </b>
            ) : (
              <Link to={to} data-testid={`bar-crumb-${idx}`}>
                {item.label}
              </Link>
            )}
          </Fragment>
        );
      })}
    </nav>
  );
}
